import { useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from "@mui/material";

interface TerminationDateDialogProps {
  open: boolean;
  /** Term bounds as YYYY-MM-DD. */
  effectiveStartDate?: string | null;
  effectiveEndDate?: string | null;
  /** Pre-fill (an already-cancelled policy being re-dated). */
  initialValue?: string | null;
  onConfirm: (terminationDate: string) => void;
  onCancel: () => void;
}

/**
 * Asked when a policy's status is changed to "cancelled" from the page header
 * (#7320, ADR-081 D5). The API requires a termination date with the status and
 * bounds it to the term; collecting it here keeps the status chip one click and
 * avoids a 400 the header could not explain.
 */
export const TerminationDateDialog = ({
  open,
  effectiveStartDate,
  effectiveEndDate,
  initialValue,
  onConfirm,
  onCancel,
}: TerminationDateDialogProps) => {
  const [value, setValue] = useState(initialValue ?? "");
  const start = effectiveStartDate?.slice(0, 10);
  const end = effectiveEndDate?.slice(0, 10);

  const error = !value
    ? "Termination date is required"
    : start && value < start
      ? "Must not be before the effective start date"
      : end && value > end
        ? "Must not be after the effective end date"
        : undefined;

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Cancel policy mid-term</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Enter the date coverage ended. Nothing incurred or paid after it counts toward the deductible. The contracted
          term dates are kept.
        </DialogContentText>
        <TextField
          autoFocus
          fullWidth
          type="date"
          label="Termination date"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          error={!!value && !!error}
          helperText={value ? error : start && end ? `Between ${start} and ${end}` : undefined}
          slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: start, max: end } }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Keep current status</Button>
        <Button variant="contained" disabled={!!error} onClick={() => onConfirm(value)}>
          Cancel policy
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TerminationDateDialog;
